import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * Sepet çekmecesi ürün önerileri.
 *
 * Tasarım notları:
 * - Tema, sepet değişince bölümü "morph" ediyor: eleman korunuyor ama çocukları
 *   siliniyor. Bu yüzden connectedCallback bir daha çalışmıyor. Kurtarma yolu
 *   olay dinleyicisi + MutationObserver ile kendini onarma.
 * - Gerçek olay adı 'shopify:cart:lines-update' (document'a bubble ediyor).
 * - Öneriler sepetteki TÜM ürünlere göre puanlanıyor; sepette zaten olan
 *   kategoriden ürün önerilmiyor (2 toner pad varken 3. toner pad önerme).
 *
 * VÀLIEN 06.09.2026 — ÖNERİLER ADET DEĞİŞİNCE SABİT KALIR
 * Sorun: 'cart:lines-update' adet değişiminde de tetikleniyor ve öneriler
 * baştan hesaplanıp değişiyordu.
 * Çözüm: sepetin "imzası" (benzersiz ürün kimlikleri, ADETLER HARİÇ) tutuluyor;
 * imza aynıysa yeniden hesaplama yapılmıyor, aynı kartlar geri basılıyor.
 *
 * ÖNEMLİ: önbellek MODÜL SEVİYESİNDE tutulur, eleman üzerinde değil.
 * Çünkü <cart-drawer-recommendations> snippets/cart-drawer.liquid içinde
 * Section Rendering API ile yeniden basilan bloğun içinde; sepet değişince
 * eleman komple değiştirilebiliyor ve eleman üzerindeki önbellek kayboluyor.
 */

const OLAY_ADLARI = ['shopify:cart:lines-update', 'cart:update'];
const GOSTERILECEK = 2;

/** @type {string} Son render edilen sepet imzası (eleman değişse de yaşar). */
let sonImza = '';
/** @type {Array|null} O imza için seçilmiş ürünler. */
let sonUrunler = null;

class CartDrawerRecommendations extends HTMLElement {
  static get observedAttributes() {
    return ['data-product-id'];
  }

  connectedCallback() {
    this.#bagla();
    this.#yukle();
  }

  disconnectedCallback() {
    (this._temizleyiciler || []).forEach((f) => f());
    this._temizleyiciler = [];
    if (this._gozlemci) this._gozlemci.disconnect();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'data-product-id' && oldValue !== newValue && oldValue !== null) {
      this.#yukle();
    }
  }

  #bagla() {
    if (this._bagli) return;
    this._bagli = true;
    this._temizleyiciler = [];

    const adlar = new Set(OLAY_ADLARI);
    // @shopify/events sabiti farklı bir ad kullanıyorsa onu da dinle.
    if (StandardEvents && typeof StandardEvents.cartLinesUpdate === 'string') {
      adlar.add(StandardEvents.cartLinesUpdate);
    }

    const tepki = () => this.#yukle();
    adlar.forEach((ad) => {
      document.addEventListener(ad, tepki);
      this._temizleyiciler.push(() => document.removeEventListener(ad, tepki));
    });

    // Tema morph edip içeriğimizi silerse kendimizi geri yükle.
    this._gozlemci = new MutationObserver(() => {
      if (this._basiyor) return;
      if (this.querySelector('.cart-drawer__rec-card')) return;
      clearTimeout(this._onarimZamani);
      this._onarimZamani = setTimeout(() => this.#yukle(), 150);
    });
    this._gozlemci.observe(this, { childList: true });
  }

  /**
   * Ürün formatı = Shopify product_type (Serum, Krem, Peeling, Set,
   * Toner Pad'ler, Yüz Sabunları, Yüz Maskeleri, Yüz Temizleyici).
   * Tip verisi 1 Eylül 2026'da 12 üründe düzeltildi; başlıktan tahmin
   * yürüten eski mantık kaldırıldı. Tip boşsa başlığa düşülür.
   */
  #format(urunVeyaBaslik, tip) {
    const t = (tip || '').trim().toLocaleLowerCase('tr');
    if (t) return t;
    const b = (urunVeyaBaslik || '').toLocaleLowerCase('tr');
    if (/\bset\b|seti/.test(b)) return 'set';
    if (/toner\s*pad/.test(b)) return 'toner';
    if (/sabun/.test(b)) return 'sabun';
    if (/maske/.test(b)) return 'maske';
    if (/peeling/.test(b)) return 'peeling';
    if (/serum/.test(b)) return 'serum';
    if (/krem/.test(b)) return 'krem';
    if (/temizle|jel/.test(b)) return 'temizleyici';
    return '';
  }

  /** Katalog tek sefer çekilip önbelleğe alınır (tip/etiket bilgisi için). */
  #katalog() {
    if (this._katalogSozu) return this._katalogSozu;
    this._katalogSozu = fetch('/products.json?limit=250', { headers: { Accept: 'application/json' } })
      .then((res) => (res.ok ? res.json() : null))
      .catch(() => null)
      .then((data) => {
        const harita = new Map();
        ((data && data.products) || []).forEach((p) => {
          harita.set(String(p.id), {
            tip: p.product_type || '',
            etiketler: p.tags || [],
            ham: p,
          });
        });
        return harita;
      });
    return this._katalogSozu;
  }

  /**
   * Sepetin öneri imzası: benzersiz ürün kimlikleri, sıralı.
   * ADETLER KASITLI OLARAK DIŞARIDA — adet değişimi öneriyi değiştirmemeli.
   */
  #imza(sepet) {
    return [...new Set(sepet.items.map((it) => String(it.product_id)))].sort().join(',');
  }

  #yukle() {
    if (this._yukleniyor) {
      this._bekleyen = true;
      return;
    }
    this._yukleniyor = true;

    Promise.all([
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null),
      this.#katalog(),
    ])
      .then(([sepet, katalog]) => {
        if (!sepet || !sepet.items || !sepet.items.length) {
          sonImza = '';
          sonUrunler = null;
          this.#bas('');
          return null;
        }

        const imza = this.#imza(sepet);

        // Sepetin ürün bileşimi değişmediyse (yalnızca adet değiştiyse)
        // AYNI önerileri kullan — yeniden hesaplama yok.
        if (imza === sonImza && sonUrunler && sonUrunler.length) {
          if (!this.querySelector('.cart-drawer__rec-card')) this.#render(sonUrunler);
          return null;
        }

        return this.#topla(sepet, katalog).then((adaylar) => {
          const siralı = this.#puanla(adaylar, sepet, katalog);
          if (siralı.length) {
            sonImza = imza;
            sonUrunler = siralı.slice(0, GOSTERILECEK);
            this.#render(sonUrunler);
          } else if (!this.querySelector('.cart-drawer__rec-card')) {
            this.#bas('');
          }
          return null;
        });
      })
      .catch(() => {})
      .finally(() => {
        this._yukleniyor = false;
        if (this._bekleyen) {
          this._bekleyen = false;
          this.#yukle();
        }
      });
  }

  /**
   * Sepetteki her ürünün "ilgili ürünler" listesini toplar.
   * Kaç farklı sepet ürününün önerdiğini de sayar (alaka sinyali).
   */
  #topla(sepet, katalog) {
    const sepetIdleri = new Set(sepet.items.map((it) => String(it.product_id)));
    const capalar = [...new Set(sepet.items.map((it) => it.product_id))].sort().slice(0, 4);
    const bulunan = new Map();

    const zincir = capalar.reduce(
      (onceki, id) =>
        onceki.then(() =>
          fetch(`/recommendations/products.json?product_id=${id}&limit=10&intent=related`)
            .then((r) => (r.ok ? r.json() : null))
            .catch(() => null)
            .then((data) => {
              ((data && data.products) || []).forEach((p) => {
                const anahtar = String(p.id);
                if (sepetIdleri.has(anahtar)) return;
                const mevcut = bulunan.get(anahtar);
                if (mevcut) {
                  mevcut.kacKezOnerildi += 1;
                } else {
                  bulunan.set(anahtar, { urun: this.#duzle(p, katalog), kacKezOnerildi: 1 });
                }
              });
            })
        ),
      Promise.resolve()
    );

    return zincir.then(() => {
      if (bulunan.size >= GOSTERILECEK) return [...bulunan.values()];

      // Havuz yetersizse katalogdan tamamla (öneriler asla boş kalmasın).
      katalog.forEach((bilgi, id) => {
        if (bulunan.size >= 6) return;
        if (sepetIdleri.has(id) || bulunan.has(id)) return;
        const stokta = (bilgi.ham.variants || []).some((v) => v.available !== false);
        if (!stokta) return;
        bulunan.set(id, { urun: this.#duzle(bilgi.ham, katalog), kacKezOnerildi: 0 });
      });
      return [...bulunan.values()];
    });
  }

  /**
   * Sepetle uyum puanı:
   *   +3  sepetteki her ürün onu önerdiyse (en güçlü sinyal)
   *   +1  sepetle paylaştığı her etiket (cilt tipi / endişe / içerik) — en fazla 3
   *   -5  sepette zaten aynı kategoriden ürün varsa (3. toner pad önerme)
   *
   * Eşit puanlı adaylarda son kırıcı ürün kimliğidir — aynı sepet için
   * hep aynı sıralama üretilir.
   */
  #puanla(adaylar, sepet, katalog) {
    const sepetFormatlari = new Set();
    const sepetEtiketleri = new Set();
    sepet.items.forEach((it) => {
      const bilgi = katalog.get(String(it.product_id));
      sepetFormatlari.add(this.#format(it.product_title || it.title, bilgi && bilgi.tip));
      (bilgi ? bilgi.etiketler || [] : []).forEach((e) => sepetEtiketleri.add(e));
    });
    sepetFormatlari.delete('');

    return adaylar
      .map((aday) => {
        const bilgi = katalog.get(String(aday.urun.id));
        const etiketler = (bilgi && bilgi.etiketler) || aday.urun.etiketler || [];
        const format = this.#format(aday.urun.title, (bilgi && bilgi.tip) || aday.urun.tip);

        let puan = aday.kacKezOnerildi * 3;
        puan += Math.min(etiketler.filter((e) => sepetEtiketleri.has(e)).length, 3);
        // Sepette zaten aynı formattan ürün varsa geri plana at:
        // 2 toner pad'i olana 3. toner pad önermek işe yaramaz.
        if (format && sepetFormatlari.has(format)) puan -= 5;

        return { ...aday, puan };
      })
      .sort((a, b) => b.puan - a.puan || String(a.urun.id).localeCompare(String(b.urun.id)))
      .map((x) => x.urun);
  }

  /** /recommendations ve /products.json farklı şekiller döndürüyor; tek şekle indirger. */
  #duzle(p, katalog) {
    const variant = (p.variants || []).find((v) => v.available !== false) || (p.variants || [])[0];
    let image = p.featured_image || '';
    if (!image && p.images && p.images[0]) {
      image = typeof p.images[0] === 'string' ? p.images[0] : p.images[0].src || '';
    }
    const price =
      typeof p.price === 'number' ? p.price : Math.round(parseFloat((variant && variant.price) || '0') * 100);
    const bilgi = katalog && katalog.get(String(p.id));

    return {
      id: p.id,
      title: p.title,
      url: p.url || (p.handle ? `/products/${p.handle}` : '#'),
      price,
      featured_image: image,
      tip: p.type || (bilgi && bilgi.tip) || '',
      etiketler: p.tags || (bilgi && bilgi.etiketler) || [],
      variants: (p.variants || []).map((v) => ({ id: v.id, available: v.available !== false })),
    };
  }

  /** innerHTML yazarken MutationObserver'ın kendini tetiklemesini engeller. */
  #bas(html) {
    this._basiyor = true;
    this.innerHTML = html;
    this._basiyor = false;
  }

  #render(products) {
    const title = this.dataset.title || 'Bunları da Beğenebilirsiniz';
    const cards = products
      .map((p) => {
        const image = p.featured_image || '';
        const price = (p.price / 100).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        const variant = (p.variants || []).find((v) => v.available) || (p.variants || [])[0];
        const safeTitle = p.title.replace(/"/g, '&quot;');
        return `
          <div class="cart-drawer__rec-card-wrap">
            <a href="${p.url}" class="cart-drawer__rec-card">
              <span class="cart-drawer__rec-image">
                ${image ? `<img src="${image}" loading="lazy" alt="${safeTitle}" width="200" height="200">` : ''}
              </span>
              <span class="cart-drawer__rec-title">${p.title}</span>
              <span class="cart-drawer__rec-price">${price} TL</span>
            </a>
            ${
              variant && variant.available
                ? `<button type="button" class="cart-drawer__rec-add" data-rec-variant-id="${variant.id}" data-rec-product-id="${p.id}"><span>SEPETE EKLE</span></button>`
                : ''
            }
          </div>
        `;
      })
      .join('');

    this.dataset.loaded = 'true';
    this.#bas(`
      <div class="cart-drawer__recommendations">
        <p class="cart-drawer__recommendations-title">${title}</p>
        <div class="cart-drawer__recommendations-grid">${cards}</div>
      </div>
    `);

    this.querySelectorAll('[data-rec-variant-id]').forEach((btn) => {
      btn.addEventListener('click', (e) => this.#addToCart(e, btn));
    });
  }

  /**
   * @param {MouseEvent} event
   * @param {HTMLElement} btn
   */
  #addToCart(event, btn) {
    event.preventDefault();
    if (btn.disabled) return;
    btn.disabled = true;
    const label = btn.querySelector('span');
    if (label) label.textContent = 'EKLENİYOR...';

    const variantId = btn.dataset.recVariantId;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = [];
    cartItemsComponents.forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
    });

    const deferredPromise = CartLinesUpdateEvent.createPromise
      ? CartLinesUpdateEvent.createPromise()
      : (() => {
          let resolve;
          let reject;
          const promise = new Promise((res, rej) => {
            resolve = res;
            reject = rej;
          });
          return { promise, resolve, reject };
        })();

    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart-drawer-recommendations',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferredPromise.promise,
      })
    );

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1, sections: sectionIds.join(',') }),
    })
      .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
      .then(({ ok, data }) => {
        if (!ok || data.status) {
          throw new Error((data && data.message) || 'Sepete eklenemedi');
        }
        return fetch('/cart.js', { headers: { Accept: 'application/json' } })
          .then((r) => r.json())
          .then((cart) => {
            deferredPromise.resolve({
              cart: CartLinesUpdateEvent.createCartFromAjaxResponse
                ? CartLinesUpdateEvent.createCartFromAjaxResponse(cart)
                : cart,
              detail: {
                items: cart.items,
                source: 'cart-drawer-recommendations',
                sourceId: this.id || 'cart-drawer-recommendations',
                itemCount: 1,
                productId: this.dataset.productId,
                sections: data.sections,
                didError: false,
              },
            });
            if (label) label.textContent = 'SEPETE EKLENDİ ✓';
          });
      })
      .catch(() => {
        deferredPromise.reject(new Error('add-to-cart-failed'));
        if (label) label.textContent = 'HATA, TEKRAR DENE';
      })
      .finally(() => {
        setTimeout(() => {
          btn.disabled = false;
          if (label) label.textContent = 'SEPETE EKLE';
        }, 2200);
      });
  }
}

if (!customElements.get('cart-drawer-recommendations')) {
  customElements.define('cart-drawer-recommendations', CartDrawerRecommendations);
}
